import React, { FC, useContext } from 'react';
import classNames from 'classnames';

import { NavigationContext } from '@buyside/components/presentation/MainSiteLayout/mainSiteLayout';
import AccessMoreContent from '@buyside/components/presentation/IdeaShowPage/AccessMoreContent/accessMoreContent';
import AuthorActions from '@buyside/components/presentation/IdeaShowPage/AuthorActions/authorActions';
import AuthorDisplay from '@buyside/components/presentation/IdeaShowPage/AuthorDisplay/authorDisplay';
import ErrorBoundary from '@buyside/components/presentation/ErrorBoundary/errorBoundary';
import IdeaOverview from '@buyside/components/presentation/IdeaShowPage/IdeaOverview/ideaOverview';
import IdeaStatusBar from '@buyside/components/presentation/IdeaShowPage/IdeaStatusBar/ideaStatusBar';
import IdeaUpdates from '@buyside/components/presentation/IdeaUpdates/ideaUpdates';
import PageNavigator from '@buyside/components/presentation/PageNavigator/pageNavigator';
import RightSidebar from '@buyside/components/presentation/IdeaShowPage/RightSidebar/rightSidebar';
import ShowPageComments from '@buyside/components/presentation/ShowPageComments/showPageComments';
import Thesis from '@buyside/components/presentation/IdeaShowPage/Thesis/thesis';

import { BREAKPOINTS, useIsMobile } from '@buyside/util/hooks/useIsMobile';

import {
  useIdeaCoreQuery,
  useIdeaAuthorQuery,
  useIdeaCurrentUserQuery,
  useIdeaPositionsQuery,
  useIdeaPositionsPricingQuery
} from '@interfaces/graphql-typings';

import styles from './ideaShowPageStyles.scss';

const createNavigatorItems = (showUpdates: boolean): { label: string; id: string }[] => {
  const items = [
    { label: 'Thesis', id: 'idea_thesis' },
    { label: 'Comments', id: 'idea_comments' }
  ];

  if (showUpdates) items.unshift({ label: 'Updates', id: 'idea_updates' });

  return items;
};

const IdeaShowPage: FC<{ ideaId: string; isPublic: boolean }> = ({ ideaId, isPublic }) => {
  const isMobile = useIsMobile(BREAKPOINTS.LARGE);

  const { sideNavigationOpen } = useContext(NavigationContext) || {};

  const {
    data: ideaAuthorData,
    loading: ideaAuthorLoading,
    error: ideaAuthorError
  } = useIdeaAuthorQuery({ variables: { ideaId } });

  const { data: ideaData, loading: ideaLoading, error: ideaError } = useIdeaCoreQuery({
    variables: { ideaId }
  });

  const { data: userData, loading: userLoading, error: userError } = useIdeaCurrentUserQuery({
    variables: { ideaId },
    skip: isPublic
  });
  const { loading: positionLoading, error: positionError } = useIdeaPositionsQuery({
    variables: { ideaId }
  });

  const { error: ideaPositionsPricingError } = useIdeaPositionsPricingQuery({
    variables: { ideaId }
  });

  const loading = ideaAuthorLoading || ideaLoading || userLoading || positionLoading;
  const error = ideaError || ideaAuthorError || positionError || ideaPositionsPricingError;
  if (error) throw error;
  if (userError) {
    if (userError.message.includes('401')) window.location.reload();
    throw userError;
  }

  const { anonymous, approved, closedAt, ideaUpdatesCount, canCurrentUserViewIdeaFilterDetails } =
    ideaData?.idea || {};
  const { authorName, isNonBuyside, user: author } = ideaAuthorData?.idea || {};
  const { id: ideaAuthorId, displayName, username } = author || {};
  const { id: currentUserId, canComment, canRateIdeas, isAdmin } = userData?.currentUser || {};

  const isAuthor = currentUserId === ideaAuthorId;
  const authorDisplayName = authorName || displayName;
  const canWriteComments = canComment || isAuthor || (!isPublic && isNonBuyside);
  const canRateIdea = Boolean(canRateIdeas) || (!isPublic && isNonBuyside);

  return (
    <div data-heap-segment="idea-show-page">
      {!isPublic ? (
        <PageNavigator
          slideIntoView
          slideIntoViewOffset={100}
          items={createNavigatorItems(Boolean(ideaUpdatesCount || isAuthor || isAdmin))}
        />
      ) : null}
      {!loading && (
        <ErrorBoundary pageName="idea status">
          <IdeaStatusBar ideaId={ideaId} isAuthor={isAuthor} />
        </ErrorBoundary>
      )}
      <section
        className={classNames(styles.twoColContainer, {
          [styles.sideNavigationOpen]: Boolean(sideNavigationOpen)
        })}
      >
        <div className={styles.leftColumn}>
          <ErrorBoundary pageName="idea overview">
            <IdeaOverview
              canRateIdea={Boolean(canRateIdea)}
              ideaId={ideaId}
              isAuthor={isAuthor}
              isPublic={isPublic}
              loading={loading}
            />
          </ErrorBoundary>
          {!isPublic && !loading && (
            <ErrorBoundary pageName="author actions">
              <AuthorActions
                approved={Boolean(approved)}
                ideaAuthorId={ideaAuthorId}
                ideaId={ideaId}
                isAdmin={Boolean(isAdmin)}
                isAuthor={isAuthor}
                isIdeaOpen={Boolean(!closedAt)}
              />
            </ErrorBoundary>
          )}
          <ErrorBoundary pageName="author display">
            <AuthorDisplay
              displayName={authorDisplayName}
              ideaId={ideaId}
              isAuthor={isAuthor}
              isPublic={isPublic}
              loading={loading}
            />
          </ErrorBoundary>
          {isMobile ? (
            <ErrorBoundary pageName="idea page">
              <RightSidebar ideaId={ideaId} isPublic={isPublic} loading={loading} />
            </ErrorBoundary>
          ) : null}
          {!canCurrentUserViewIdeaFilterDetails && !loading && (
            <ErrorBoundary pageName="idea access">
              <AccessMoreContent ideaId={ideaId} isPublic={isPublic} />
            </ErrorBoundary>
          )}
          <ErrorBoundary pageName="idea updates">
            <IdeaUpdates
              authorDisplayName={isNonBuyside ? authorDisplayName : null}
              canWriteComments={Boolean(canWriteComments)}
              ideaId={ideaId}
              isAuthor={isAuthor}
              isPublic={isPublic}
              loading={loading}
            />
          </ErrorBoundary>
          <ErrorBoundary pageName="idea thesis">
            <Thesis
              ideaId={ideaId}
              isSelectable={Boolean(isAuthor && approved)}
              loading={loading}
            />
          </ErrorBoundary>
          <ErrorBoundary pageName="idea comments">
            <ShowPageComments
              authorDisplayName={isNonBuyside ? authorDisplayName : null}
              authorTag={
                anonymous
                  ? username
                    ? `@[${username}](AnonUser-${username})`
                    : ''
                  : `@[${authorDisplayName}](User-${ideaAuthorId})`
              }
              canRateIdea={Boolean(canRateIdea)}
              canWriteComments={Boolean(canWriteComments)}
              ideaId={ideaId}
              isAuthor={isAuthor}
              loading={loading}
            />
          </ErrorBoundary>
          <div className={styles.disclaimer}>**Pricing data provided by FactSet Systems, Inc</div>
        </div>
        {isMobile ? null : (
          <ErrorBoundary pageName="idea page">
            <RightSidebar ideaId={ideaId} isPublic={isPublic} loading={loading} />
          </ErrorBoundary>
        )}
      </section>
    </div>
  );
};

export default IdeaShowPage;
